import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { consentApi, fmtError } from "@/lib/api";
import { ShieldCheck, Check, Loader2, AlertCircle } from "lucide-react";

/**
 * Modulo consensi GDPR che la cliente apre col QR mostrato dal centro.
 * Pagina pubblica (nessun login) e raggiungibile da qualsiasi rete: il centro
 * genera la sessione dal gestionale, qui la cliente sceglie e conferma.
 */
export default function ConsentPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    consentApi
      .get(token)
      .then(setData)
      .catch((e) => setError(fmtError(e)))
      .finally(() => setLoading(false));
  }, [token]);

  const keyOf = (o) => `${o.purpose}|${o.channel}`;
  const toggle = (o) => setSelected((s) => ({ ...s, [keyOf(o)]: !s[keyOf(o)] }));

  const submit = async () => {
    setSending(true);
    setError("");
    try {
      const selections = (data.options || [])
        .filter((o) => selected[keyOf(o)])
        .map((o) => ({ purpose: o.purpose, channel: o.channel }));
      await consentApi.submit(token, selections);
      setDone(true);
    } catch (e) {
      setError(fmtError(e));
    } finally {
      setSending(false);
    }
  };

  const Wrap = ({ children }) => (
    <div className="min-h-screen bg-[#FAFAFA] flex items-start justify-center px-4 py-10">
      <div className="w-full max-w-md">{children}</div>
    </div>
  );

  if (loading) {
    return (
      <Wrap>
        <div className="flex justify-center py-16">
          <Loader2 className="animate-spin text-[#A78BFA]" size={28} />
        </div>
      </Wrap>
    );
  }

  if (error && !data) {
    return (
      <Wrap>
        <div className="rounded-3xl bg-white border border-black/5 p-8 text-center" data-testid="consent-error">
          <AlertCircle className="mx-auto text-[#B91C1C] mb-3" size={28} />
          <h1 className="font-serif text-2xl text-[#1A1A1A]">Link non valido</h1>
          <p className="mt-2 text-sm text-[#4A4A4A] font-light">{error}</p>
          <p className="mt-4 text-xs text-[#737373]">Chiedi al centro di mostrarti un nuovo QR.</p>
        </div>
      </Wrap>
    );
  }

  if (done) {
    return (
      <Wrap>
        <div className="rounded-3xl bg-white border border-black/5 p-8 text-center" data-testid="consent-done">
          <div className="mx-auto h-14 w-14 rounded-full bg-gradient-to-br from-[#A78BFA] to-[#67E8F9] flex items-center justify-center mb-4">
            <Check className="text-white" size={26} />
          </div>
          <h1 className="font-serif text-3xl text-[#1A1A1A]">Grazie ✨</h1>
          <p className="mt-3 text-[#4A4A4A] font-light">
            Le tue preferenze sono state registrate. Puoi chiudere questa pagina.
          </p>
          <p className="mt-4 text-xs text-[#737373]">
            Potrai revocarle in qualsiasi momento contattando {data?.center}.
          </p>
        </div>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <div className="rounded-3xl bg-white border border-black/5 p-7" data-testid="consent-form">
        <div className="flex items-center gap-2 text-[#A78BFA] mb-3">
          <ShieldCheck size={18} />
          <span className="text-[11px] tracking-[0.25em] uppercase">Privacy</span>
        </div>
        <h1 className="font-serif font-light text-3xl text-[#1A1A1A] leading-tight">
          {data.customer_name ? `Ciao ${data.customer_name},` : "Ciao,"}
        </h1>
        <p className="mt-3 text-sm text-[#4A4A4A] font-light">
          <strong>{data.center}</strong> vorrebbe restare in contatto con te. Scegli tu come:
          puoi selezionare solo ciò che ti interessa, oppure niente.
        </p>

        <div className="mt-6 space-y-2">
          {(data.options || []).map((o) => {
            const on = !!selected[keyOf(o)];
            return (
              <button
                key={keyOf(o)}
                type="button"
                onClick={() => toggle(o)}
                data-testid={`consent-opt-${o.purpose}-${o.channel}`}
                className={`w-full text-left rounded-2xl border p-4 transition-all flex items-start gap-3 ${
                  on ? "border-[#1A1A1A] bg-[#1A1A1A] text-white" : "border-black/10 bg-white hover:border-[#A78BFA]/50"
                }`}
              >
                <span
                  className={`mt-0.5 h-5 w-5 rounded-md border flex items-center justify-center shrink-0 ${
                    on ? "bg-white border-white" : "border-black/20"
                  }`}
                >
                  {on && <Check size={14} className="text-[#1A1A1A]" />}
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium">{o.titolo}</span>
                  {o.desc && (
                    <span className={`block text-xs mt-0.5 ${on ? "text-white/70" : "text-[#737373]"}`}>{o.desc}</span>
                  )}
                </span>
              </button>
            );
          })}
        </div>

        {error && <div className="mt-4 text-sm text-[#B91C1C]">{error}</div>}

        <button
          onClick={submit}
          disabled={sending}
          data-testid="consent-submit"
          className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#1A1A1A] hover:bg-[#333] disabled:opacity-50 text-white rounded-full px-6 py-3.5 text-sm tracking-wide transition-all"
        >
          {sending && <Loader2 size={16} className="animate-spin" />}
          Conferma le mie scelte
        </button>

        <p className="mt-4 text-[11px] text-[#737373] leading-relaxed text-center">
          I consensi sono facoltativi e revocabili in qualsiasi momento, senza conseguenze
          sui trattamenti che ricevi. Titolare del trattamento è {data.center}, che li
          gestisce secondo la propria informativa privacy
          {data.privacy_policy_version ? ` (vers. ${data.privacy_policy_version})` : ""}.
          La piattaforma è fornita da AURA, che agisce come responsabile del trattamento:
          vedi la{" "}
          <a
            href="/privacy"
            target="_blank"
            rel="noreferrer noopener"
            className="underline underline-offset-2"
          >
            nostra informativa
          </a>
          . Per registrare la scelta conserviamo data, ora, indirizzo IP e browser usato.
        </p>
      </div>
    </Wrap>
  );
}
